import React, { useState, useCallback } from 'react';
import './App.css';
import { RegistrationFormData, FormStep } from './types/registration';
import ProgressBar from './components/ProgressBar';
import Step1LoginData from './components/Step1LoginData';
import Step2PersonalData from './components/Step2PersonalData';
import Step3ProfessionalData, { getDbValuesForGroup } from './components/Step3ProfessionalData';
import SuccessPage from './components/SuccessPage';
import { t } from './services/translation';

// ===================== INITIAL STATE =====================
const initialFormData: RegistrationFormData = {
  // Step 1
  email: '',
  password: '',
  passwordConfirm: '',
  privacyAccepted: false,
  recaptchaToken: '',
  recaptchaVerifiedToken: '',
  website: '',
  // Step 2
  lastName: '',
  firstName: '',
  phone: '',
  birthYear: '',
  gender: '',
  languagePreference: 'magyar',
  // Step 3
  mernokMuszakiTeruletek: [],
  informatikaiTeruletek: [],
  penzugyTeruletek: [],
  kereskedelemTeruletek: [],
  adminisztracisTeruletek: [],
  szakiranyuTeruletek: [],
  primaryArea: '',
  experienceLevel: '',
  // Hidden
  finishedTab: '',
  utmDatas: '',
  events: ['jobverse-allasborze'],
  regLang: 'hu',
};

// ===================== UTM helper =====================
function getUtmDatas(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(k => {
      const v = params.get(k);
      if (v) utm[k] = v;
    });
    return Object.keys(utm).length ? JSON.stringify(utm) : '';
  } catch {
    return '';
  }
}

// ===================== DATA LAYER HELPER =====================
function pushDataLayer(data: Record<string, any>) {
  try {
    if (typeof window !== 'undefined') {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push(data);
    }
  } catch (e) {
    console.error('dataLayer push error:', e);
  }
}

// ===================== APP =====================
const App: React.FC = () => {
  const [step, setStep] = useState<FormStep>(1);
  const [formData, setFormData] = useState<RegistrationFormData>({
    ...initialFormData,
    utmDatas: getUtmDatas(),
    regLang: document.documentElement.lang?.startsWith('en') ? 'en' : 'hu',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [step1ServerError, setStep1ServerError] = useState('');

  const apiSettings = window.registrationApiSettings;

  const handleChange = useCallback((fields: Partial<RegistrationFormData>) => {
    setFormData(prev => ({ ...prev, ...fields }));
  }, []);

  // ---- Analytics / dataLayer tracking on step view ----
  React.useEffect(() => {
    if (!isSuccess) {
      pushDataLayer({
        event: 'registration_step_view',
        step_number: step,
        step_name: step === 1 ? 'login_data' : step === 2 ? 'personal_data' : 'professional_data',
      });
    } else {
      pushDataLayer({
        event: 'registration_success',
        step_number: 4,
        step_name: 'success',
      });
    }
  }, [step, isSuccess]);

  // ---- Step 1 → Step 2: email unique check ----
  const handleStep1Next = async () => {
    setStep1ServerError('');
    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append('action', 'jw_pwa_check_email');
      fd.append('nonce', apiSettings.nonce);
      fd.append('email', formData.email);
      fd.append('recaptcha_token', formData.recaptchaToken);
      fd.append('website', formData.website || '');

      const res = await fetch(apiSettings.ajaxUrl, { method: 'POST', body: fd });
      const json = await res.json();

      if (json.success) {
        if (json.data?.verified_token) {
          setFormData(prev => ({ ...prev, recaptchaVerifiedToken: json.data.verified_token }));
        }
        pushDataLayer({ event: 'registration_step_complete', step_number: 1, step_name: 'login_data' });
        setStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setStep1ServerError(t(json.data?.message || 'Ez az e-mail cím már foglalt.'));
      }
    } catch {
      setStep1ServerError(t('Hálózati hiba. Kérjük, próbáld újra.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleStep2Next = () => {
    pushDataLayer({ event: 'registration_step_complete', step_number: 2, step_name: 'personal_data' });
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ---- Final submit ----
  const handleSubmit = async () => {
    setGlobalError('');
    setIsLoading(true);

    try {
      const fd = new FormData();
      fd.append('action', 'jw_pwa_register');
      fd.append('nonce', apiSettings.nonce);

      // Step 1
      fd.append('email', formData.email);
      fd.append('password', formData.password);
      fd.append('recaptcha_token', formData.recaptchaToken);
      fd.append('recaptcha_verified_token', formData.recaptchaVerifiedToken || '');
      fd.append('website', formData.website || '');

      // Step 2
      fd.append('last_name', formData.lastName);
      fd.append('first_name', formData.firstName);
      fd.append('phone', formData.phone);
      fd.append('birth_year', formData.birthYear);
      fd.append('gender', formData.gender);
      fd.append('language_preference', formData.languagePreference);

      // Step 3 – területek (serialize as DB JSON values)
      fd.append('mernok_muszaki', JSON.stringify(getDbValuesForGroup('mernokMuszakiTeruletek', formData.mernokMuszakiTeruletek)));
      fd.append('informatikai', JSON.stringify(getDbValuesForGroup('informatikaiTeruletek', formData.informatikaiTeruletek)));
      fd.append('penzugy', JSON.stringify(getDbValuesForGroup('penzugyTeruletek', formData.penzugyTeruletek)));
      fd.append('kereskedelem', JSON.stringify(getDbValuesForGroup('kereskedelemTeruletek', formData.kereskedelemTeruletek)));
      fd.append('adminisztracio', JSON.stringify(getDbValuesForGroup('adminisztracisTeruletek', formData.adminisztracisTeruletek)));
      fd.append('szakiranyu', JSON.stringify(getDbValuesForGroup('szakiranyuTeruletek', formData.szakiranyuTeruletek)));
      fd.append('primary_area', formData.primaryArea);
      fd.append('experience_level', formData.experienceLevel);

      // Hidden
      fd.append('utm_datas', formData.utmDatas);
      fd.append('events', JSON.stringify(formData.events));
      fd.append('reg_lang', formData.regLang);

      const res = await fetch(apiSettings.ajaxUrl, { method: 'POST', body: fd });
      const json = await res.json();

      if (json.success) {
        pushDataLayer({ event: 'registration_step_complete', step_number: 3, step_name: 'professional_data' });
        if (json.data?.redirect) {
          window.location.href = json.data.redirect;
        } else {
          setIsSuccess(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        const msg = t(json.data?.message || 'Hiba történt a regisztráció során. Kérjük, próbáld újra.');
        const field = json.data?.field;
        if (field === 'email') {
          setStep(1);
          setStep1ServerError(msg);
        } else {
          setGlobalError(msg);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {
      setGlobalError(t('Hálózati hiba. Kérjük, ellenőrizd az internetkapcsolatodat és próbáld újra.'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  // ===================== RENDER =====================
  return (
    <div className="jw-reg-pwa">
      <div className="jw-reg-container">
        <div className="jw-reg-card">

          {/* HEADER */}
          {!isSuccess && (
            <div className="jw-reg-header">
              <h1>{t('JOBVERSE Látogatói regisztráció')}</h1>
              <p>{t('Töltsd ki az alábbi mezőket a regisztrációhoz.')}</p>
            </div>
          )}

          {/* PROGRESS */}
          {!isSuccess && <ProgressBar currentStep={step} />}

          {/* GLOBAL ERROR */}
          {globalError && !isSuccess && (
            <div className="jw-global-error" role="alert">
              <span className="jw-global-error-icon">⚠</span>
              <span dangerouslySetInnerHTML={{ __html: globalError }} />
            </div>
          )}

          {/* STEPS */}
          {isSuccess ? (
            <SuccessPage />
          ) : step === 1 ? (
            <Step1LoginData
              data={formData}
              onChange={handleChange}
              onNext={handleStep1Next}
              globalError={step1ServerError}
              siteKey={apiSettings?.siteKey}
            />
          ) : step === 2 ? (
            <Step2PersonalData
              data={formData}
              onChange={handleChange}
              onNext={handleStep2Next}
              onBack={() => setStep(1)}
            />
          ) : (
            <Step3ProfessionalData
              data={formData}
              onChange={handleChange}
              onSubmit={handleSubmit}
              onBack={() => setStep(2)}
              isLoading={isLoading}
            />
          )}

        </div>
      </div>
    </div>
  );
};

export default App;
